import React from 'react';
import { useMemo, useState } from 'react';
import ReactModal from 'react-modal';
// @ts-ignore
import toast from 'react-hot-toast';
import { CheckBox } from '../../../Project/Funds';
import Button from '../../../../components/Button';
import TickCircle from '../../../../assets/icons/tick-circle-2.svg?react';
import CloseCircle from '../../../../assets/icons/circle-close-2.svg?react';
import {
  Wrapper,
  Body,
  SelectAllStyle,
  Buttons,
  Title,
  Funds,
  FundItem,
  FundsContainer
} from './style';
import { useSubmitReport } from '../../../../services/report.service';
import { statusKeys } from '../Funds/consts';

interface Fund {
  fund_id: any;
  id: any;
  fund_name: string;
  report_status?: {
    latest_status?: string;
  };
}

interface SubmitReportModalProps {
  id?: string;
  isOpen: boolean;
  onClose: () => void;
  periodId?: string;
  funds?: Fund[];
  refetchFunds: () => void;
}

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    padding: 0,
    border: 'none',
    borderRadius: '24px'
  },
  overlay: {
    background: 'rgba(0, 0, 0, 0.5)',
    zIndex: '9999'
  }
};

const icons = {
  [statusKeys.READY]: <TickCircle />,
  [statusKeys.SUBMITTED]: <TickCircle />,
  [statusKeys.ERROR]: <CloseCircle />
};

const labels = {
  [statusKeys.READY]: 'Ready',
  [statusKeys.SUBMITTED]: 'Submitted',
  [statusKeys.ERROR]: 'Errors'
};

const priorities = {
  [statusKeys.READY]: 2,
  [statusKeys.ERROR]: 1,
  [statusKeys.SUBMITTED]: 0
};

const SubmitReportModal = ({
  id,
  isOpen,
  onClose,
  periodId,
  funds = [],
  refetchFunds
}: SubmitReportModalProps) => {
  // @ts-ignore e
  const submitReport = useSubmitReport();
  const [fundIds, setFundIds] = useState<any[]>([]);

  const _funds = useMemo<any[]>(() => {
    return funds.sort(
      (a, b) =>
        // @ts-ignore
        (priorities[b.report_status?.latest_status as any] || 0) -
        (priorities[a.report_status?.latest_status as any] || 0)
    );
  }, [funds]);

  const onSelectFund = (id: any): void => {
    if (fundIds.includes(id)) {
      setFundIds(fundIds.filter(val => val !== id));
    } else {
      setFundIds([...fundIds, id]);
    }
  };

  const onSelectAll = (): void => {
    if (fundIds.length === _funds.length) {
      setFundIds([]);
    } else {
      setFundIds(_funds.map(item => item.id));
    }
  };

  const onCancel = (): void => {
    onClose();
    setFundIds([]);
  };

  const onSubmitClick = (): void => {
    const payload = {
      period_id: periodId,
      fund_id_list: fundIds
    };

    submitReport.mutate(payload, {
      onSuccess: () => {
        onCancel();
        refetchFunds();
        toast.success('Reports have been submitted successfully!', {
          duration: 5000
        });
      },
      onError: () => {
        toast.error('Error on submitting the report!');
      }
    });
  };

  return (
    <ReactModal
      id={id ?? 'submit-report-modal'}
      isOpen={isOpen}
      onRequestClose={onCancel}
      contentLabel="Submit Modal"
      style={customStyles}
      bodyOpenClassName="overflow-hidden"
    >
      <Wrapper id="submit-report-modal-wrapper">
        <Body id="submit-report-modal-body">
          <Title id="submit-report-modal-title">Fund names: {_funds.length}</Title>
          <SelectAllStyle id="submit-report-modal-select-all">
            <div
              onClick={onSelectAll}
              className="select-all"
              id="submit-report-modal-select-all-row"
            >
              {/* @ts-ignore*/}
              <CheckBox
                id="submit-report-modal-select-all-checkbox"
                checked={fundIds.length === _funds.length}
              />
              Select all
            </div>
            <p id="submit-report-modal-selected-count">Selected: {fundIds.length}</p>
          </SelectAllStyle>
          <FundsContainer id="submit-report-modal-funds-container">
            <Funds id="submit-report-modal-funds-list">
              {_funds.map((fund: Fund) => {
                const status = fund.report_status?.latest_status as any;
                const isDisabled = status == statusKeys.ERROR;

                return (
                  <FundItem
                    id={`submit-report-modal-fund-item-${fund.fund_id}`}
                    key={fund.fund_id}
                    // @ts-ignore
                    status={status}
                    onClick={() => !isDisabled && onSelectFund(fund.fund_id)}
                    disabled={isDisabled}
                  >
                    {/* @ts-ignore*/}
                    <CheckBox
                      id={`submit-report-modal-fund-checkbox-${fund.fund_id}`}
                      checked={fundIds.includes(fund.fund_id)}
                    />
                    <p>{fund.fund_name}</p>
                    {status && (
                      <div
                        className="status-icon"
                        id={`submit-report-modal-status-icon-${fund.fund_id}`}
                      >
                        {labels[status]}
                        {icons[status]}
                      </div>
                    )}
                  </FundItem>
                );
              })}
            </Funds>
          </FundsContainer>
          <Buttons id="submit-report-modal-buttons">
            <Button id="submit-report-modal-cancel-button" variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              id="submit-report-modal-submit-button"
              disabled={fundIds.length === 0}
              isLoading={submitReport.isLoading}
              onClick={onSubmitClick}
            >
              Submit Reports
            </Button>
          </Buttons>
        </Body>
      </Wrapper>
    </ReactModal>
  );
};

export default SubmitReportModal;
